import { useHashRoute } from "../routing";
import { useAdminCapability } from "../useAdminCapability";
import { useT } from "../../i18n";
import { MarketplaceTabs } from "./MarketplaceTabs";
import { LandTab } from "./LandTab";
import { LandDetail } from "./LandDetail";
import { ArtworksTab } from "./ArtworksTab";
import { ArtworkDetail } from "./ArtworkDetail";
import { MyMarketplaceTab } from "./MyMarketplaceTab";
import { MarketplaceNotifications } from "./MarketplaceNotifications";
import { MarketplaceSearch } from "./MarketplaceSearch";
import { OwnerConsole } from "./OwnerConsole";
import { PreviewDemoGrid } from "./PreviewDemoGrid";
import { useMarketplaceConfig } from "./useMarketplace";
import { parseMarketplaceRoute, goMarketplace, type MarketplaceRouteState } from "./mkt-routing";

function MarketplaceContent({ route }: { route: MarketplaceRouteState }) {
  if (route.query.sell === "demo") return <PreviewDemoGrid />;
  if (route.detail?.kind === "land") return <LandDetail orderHash={route.detail.id} route={route} />;
  if (route.detail?.kind === "artwork") return <ArtworkDetail artworkId={route.detail.id} route={route} />;

  switch (route.tab) {
    case "home":
      return <MarketplaceHome />;
    case "land":
      return <LandTab route={route} />;
    case "artworks":
      return <ArtworksTab route={route} />;
    case "mine":
      return <MyMarketplaceTab />;
    case "owner":
      return <OwnerConsole />;
    default:
      return <MarketplaceHome />;
  }
}

function MarketplaceHome() {
  const t = useT();
  const cards: { k: MarketplaceRouteState["tab"]; title: string; desc: string }[] = [
    { k: "land", title: t("marketplace.home.cardLandTitle"), desc: t("marketplace.home.cardLandDesc") },
    { k: "artworks", title: t("marketplace.home.cardArtworksTitle"), desc: t("marketplace.home.cardArtworksDesc") },
    { k: "mine", title: t("marketplace.home.cardMineTitle"), desc: t("marketplace.home.cardMineDesc") },
  ];
  return (
    <div data-testid="marketplace-home" className="space-y-10">
      <section className="neon-glass-strong rounded-2xl border border-border-strong p-8 md:p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(55,224,200,0.18), transparent 45%)" }} />
        <div className="relative">
          <p className="font-mono text-[11px] tracking-[0.32em] uppercase text-neon-teal/80">{t("marketplace.home.eyebrow")}</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-text-primary tracking-tight max-w-2xl">
            {t("marketplace.home.title")}
          </h1>
          <p className="mt-4 font-mono text-[12px] leading-relaxed text-text-secondary max-w-xl">
            {t("marketplace.home.subtitle")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button type="button" data-testid="home-cta-land" onClick={() => goMarketplace("land")}
              className="h-11 px-6 rounded-md bg-neon-teal/15 text-neon-teal border border-neon-teal/50 hover:bg-neon-teal/25 hover:shadow-glow-teal-sm font-mono text-[11px] font-semibold tracking-[0.16em] uppercase transition-[background-color,box-shadow]">
              {t("marketplace.home.browseLand")}
            </button>
            <button type="button" data-testid="home-cta-artworks" onClick={() => goMarketplace("artworks")}
              className="h-11 px-6 rounded-md bg-transparent text-text-secondary border border-border-divider hover:text-text-primary hover:border-border-strong font-mono text-[11px] font-semibold tracking-[0.16em] uppercase transition-colors">
              {t("marketplace.home.browseArtworks")}
            </button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {cards.map((c) => (
          <button key={c.k} type="button" data-testid={`home-nav-${c.k}`}
            onClick={() => goMarketplace(c.k)}
            className="text-left neon-glass rounded-xl border border-border-divider p-6 hover:border-neon-teal/40 hover:shadow-glow-teal-sm transition-[border-color,box-shadow]">
            <span className="font-display text-lg text-text-primary">{c.title}</span>
            <p className="mt-2 font-mono text-[11px] leading-relaxed text-text-muted">{c.desc}</p>
          </button>
        ))}
      </section>
    </div>
  );
}

export function MarketplaceRoute() {
  const t = useT();
  const hash = useHashRoute();
  const route = parseMarketplaceRoute(hash) ?? { tab: "home" as const, detail: null, query: {} };
  const { data: config } = useMarketplaceConfig();
  const { isAdmin } = useAdminCapability();

  return (
    <div data-testid="marketplace-route" className="mx-auto w-full max-w-7xl px-4 md:px-8 py-8">
      <div className="mb-8 border-b border-border-divider pb-4 flex items-center justify-between gap-4">
        <MarketplaceTabs active={route.detail?.kind === "land" ? "land" : route.detail?.kind === "artwork" ? "artworks" : route.tab} isAdmin={isAdmin} />
        <div className="flex items-center gap-1 shrink-0">
          <MarketplaceSearch />
          <MarketplaceNotifications />
        </div>
      </div>

      {config && !config.enabled && (
        <div data-testid="marketplace-paused" className="mb-6 rounded-lg border border-state-warning/40 bg-state-warning/10 px-4 py-3">
          <p className="font-mono text-[11px] text-state-warning uppercase tracking-[0.14em]">{t("marketplace.paused.title")}</p>
          <p className="mt-1 font-mono text-[10.5px] text-text-muted">{t("marketplace.paused.body")}</p>
        </div>
      )}

      <MarketplaceContent route={route} />
    </div>
  );
}

export default MarketplaceRoute;
